import React from 'react';
import { Modal, Stack, Text, Textarea, Group, Card } from '@mantine/core';
import { type z } from 'zod';
import { type taskSchema } from '@/schemas/API/common/basic/schedule';
import dayjs from 'dayjs';
import { dateFormatUI } from '@/index';

export type TaskType = z.infer<typeof taskSchema>;

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task?: TaskType | null;
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  onClose,
  task,
}) => {
  if (!task) return null;

  const date = dayjs(task.date).format(dateFormatUI);
  const startTime = dayjs(`1970-01-01T${task.startTime}`).format('HH:mm');
  const endTime = dayjs(`1970-01-01T${task.startTime}`)
    .add(task.duration, 'minute')
    .format('HH:mm');

  const address = task.address;

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Detail úkolu"
      size="50rem"
      padding="xl"
    >
      <Stack gap="sm">
        {/* Task Name */}
        <Text fw={700} size="md">
          Název úkolu
        </Text>
        <Textarea
          value={task.name}
          readOnly
          variant="filled"
          radius="md"
          autosize
          minRows={1}
        />

        {/* Client Name */}
        <Text fw={700} size="md">
          Jméno klienta
        </Text>
        <Textarea
          value={`${task.client.firstName} ${task.client.lastName}`}
          readOnly
          variant="filled"
          radius="md"
          autosize
          minRows={1}
        />

        {/* Date */}
        <Text fw={700} size="md">
          Datum
        </Text>
        <Textarea
          value={date}
          readOnly
          variant="filled"
          radius="md"
          autosize
          minRows={1}
        />

        {/* Start and End Time */}
        <Group grow align="start">
          <Stack>
            <Text fw={700} size="md">
              Začátek
            </Text>
            <Textarea
              value={startTime}
              readOnly
              variant="filled"
              radius="md"
              autosize
              minRows={1}
            />
          </Stack>
          <Stack>
            <Text fw={700} size="md">
              Konec
            </Text>
            <Textarea
              value={endTime}
              readOnly
              variant="filled"
              radius="md"
              autosize
              minRows={1}
            />
          </Stack>
        </Group>

        {/* Note */}
        <Text fw={700} size="md">
          Poznámka
        </Text>
        <Textarea
          value={task.note || 'Žádné poznámky'}
          readOnly
          variant="filled"
          radius="md"
          autosize
          minRows={1}
        />

        {/* Address Card */}
        <Card padding="md" radius="md" withBorder>
          <Text fw={700} size="md" mb="sm">
            Adresa
          </Text>
          <Stack gap="sm">
            <Textarea
              variant="filled"
              radius="md"
              value={address.street}
              readOnly
              autosize
              label="Ulice"
              minRows={1}
            />
            <Group grow>
              <Textarea
                variant="filled"
                radius="md"
                value={address.descNum?.toString() || ''}
                readOnly
                autosize
                label="Č.p"
                minRows={1}
              />
              <Textarea
                variant="filled"
                radius="md"
                value={address.orientNum?.toString() || ''}
                readOnly
                autosize
                label="Č.o"
                minRows={1}
              />
            </Group>
            <Textarea
              variant="filled"
              radius="md"
              value={address.city}
              readOnly
              autosize
              label="Město"
              minRows={1}
            />
            <Textarea
              variant="filled"
              radius="md"
              value={address.postCode}
              readOnly
              autosize
              label="PSČ"
              minRows={1}
            />
          </Stack>
        </Card>
      </Stack>
    </Modal>
  );
};
